import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSchemeClasses } from './ColorSchemeProvider';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useIsBroker } from '../hooks/useIsBroker';
import { useIsSuperAdmin } from '../hooks/useIsSuperAdmin';
import { useIsAdminOrAig } from '../hooks/useIsAdminOrAig';
import { useRequestTrackerTier } from '../hooks/useRequestTrackerTier';
import { useCanSeeListings } from './listings/useListingsAccess';
import { useCanSeePropertyManagement } from './property-management/usePropertyManagementAccess';
import { useCanSeeForms, useCanStartForms } from './forms/useFormsAccess';
import { useCanOpenSettingsHub, useVisibleSettingsEntries } from './settings/useVisibleSettingsEntries';
import { useFieldChangeSuggestionsCount } from '../api/generated_hooks/generated-field_change_suggestions-hooks';
import { useBrokerSubmissionsCount } from '../api/generated_hooks/generated-broker_submissions-hooks';
import { useLookupSubmissionStatusesList } from '../api/generated_hooks/generated-lookup_submission_statuses-hooks';
import { useTrackerFunnel } from '../api/extensions/tracker/trackerFunnel';
import {
  HomeIcon,
  UserGroupIcon,
  IdentificationIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  // DocumentTextIcon — was used for the now-removed Broker Portal +
  // commented-out Documents sidebar entries. Re-import if Documents
  // ever returns.
  ChartBarIcon,
  WrenchScrewdriverIcon,
  ArrowUpTrayIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  RectangleStackIcon,
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  DocumentPlusIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Catalog feature key this sidebar entry surfaces. Visibility is
   * resolved through `useFeatureAccess`, which reads the user's
   * `feature:<key>` permission so the Feature Access matrix at
   * /settings/permissions is authoritative. A bootstrap fallback in
   * the hook keeps existing tier behaviour intact for users whose
   * roles have not been seeded yet. Absent: the entry is only added
   * when its own access rule allows it (Listings, Property management,
   * Forms, the request tracker, the developer tools), so no catalog key
   * hides it — `dashboard` used to stand in here, and switching the
   * Dashboard off hid them all.
   */
  featureKey?: string;
  isDeveloperOnly?: boolean;
  /**
   * Optional kind discriminator that picks a badge hook in the
   * SidebarMenuItem child. Kept as a string tag (not a hook ref) so
   * the menu items array stays serialisable and the hook is called
   * unconditionally per row — never inside a loop on the parent.
   */
  badgeKind?: 'pending_submissions' | 'pending_broker_submissions' | 'tracker_needs_confirmation';
}

function Sidebar() {
  // Sidebar is collapsed by default (icons only, 64px wide) and expands
  // to 256px while the mouse is over it. Hover state is driven by
  // mousemove hit-testing rather than React's onMouseEnter/Leave because
  // the synthetic events are unreliable when the window loses focus —
  // the user could alt-tab away while hovered and the sidebar would
  // stay expanded.
  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = isHovered;
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Hover state is driven by mousemove hit-testing, but only while the
    // window is focused. Without the focus gate, the user could alt-tab
    // away while hovered and the sidebar would stay expanded.
    let isFocused = document.hasFocus();
    const handleMouseMove = (e: MouseEvent) => {
      if (!isFocused || !sidebarRef.current) return;
      const rect = sidebarRef.current.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      setIsHovered(inside);
    };
    const onFocus = () => {
      isFocused = true;
    };
    const onBlur = () => {
      isFocused = false;
      setIsHovered(false);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const scheme = useSchemeClasses();
  // We intentionally don't read `role` here anymore — visibility is
  // resolved per-item through `useFeatureAccess`, which consults the
  // user's `feature:<key>` permissions (with a tier fallback for the
  // pre-seed bootstrap state). Exception: brokers get a single-item
  // sidebar (Dashboard only) since their entire navigation surface
  // is the role-aware dashboard itself — see useIsBroker below.
  const isBroker = useIsBroker();
  const canSeeListings = useCanSeeListings();
  const canSeePropertyManagement = useCanSeePropertyManagement();
  // Forms card 11: the catalog has a type the caller may submit or process (brokers too).
  const canSeeForms = useCanSeeForms();
  // Forms+ 08: "Start a form" (/forms/start) when the caller may start some type.
  const canStartForms = useCanStartForms();
  // F-ER (2026-06-26): super-admin-only "Developer" surface for the
  // reported-errors page. Whitelist lives in
  // @aiga/app-config/security and is shared with the
  // backend, so the sidebar entry only appears for users whose email
  // would also be accepted by the API.
  const isSuperAdmin = useIsSuperAdmin();
  // M286: Admin Info and Calculated Fields left the Settings hub for /devtools — an admin role or AIG (AdminOrAigRoute).
  const isAdminOrAig = useIsAdminOrAig();
  // F3 (2026-08-31): unified request-tracker. Tier-gated (dev|client) via
  // @aiga/app-config/security — NOT isSuperAdmin — so a
  // future client tier sees the entry too. CLIENT list is empty today, so
  // only DEV/AIG resolve non-null.
  const requestTrackerTier = useRequestTrackerTier();
  // M105: the /comps/reports entry reads "Comp Intelligence" for every
  // role (it used to be "All Reports" / "My Reports" by tier).
  const reportsLabel = 'Comp Intelligence';

  // Menu ordering per client request (2026-04-24, updated 2026-04-28 R26,
  // updated 2026-06-12: Broker Portal removed — dashboard is now
  // role-aware and renders a Broker-tier layout when the logged-in user
  // has the Broker role):
  //   Dashboard → Properties → Deals → Comp Intelligence → Reports
  //   → Documents → Ops Dashboard → Data Ingestion → Manage Users
  //   → Settings
  //
  // R26 (2026-04-28): Transactions absorbed into /comps?tab=transactions.
  // The standalone /transactions sidebar entry was removed; bookmarks still
  // resolve via the redirect in App.tsx.
  //
  // Tier gating rules:
  //   - `any`       = visible to every authenticated user (Member/Viewer
  //                   up through Super Admin)
  //   - `manager+`  = Manager, Admin, Super Admin
  //   - `admin+`    = Admin, Super Admin only
  //   - Base rule: anything NOT explicitly opened to a lower tier is
  //     locked to admin+ (Reports, Documents, Ops Dashboard).
  //
  // Ops Dashboard kept in the list for admin-tier users — the client
  // wants it reorderable rather than removed.
  const hasSettingsEntries = useVisibleSettingsEntries().length > 0;
  const canOpenSettingsHub = useCanOpenSettingsHub();
  const menuItems: MenuItem[] = [
    { path: '/', label: 'Dashboard', icon: HomeIcon, featureKey: 'dashboard' },
    { path: '/properties', label: 'Properties', icon: BuildingOfficeIcon, featureKey: 'properties' },
    { path: '/deals', label: 'Deals', icon: BriefcaseIcon, featureKey: 'deals' },
    { path: '/comps', label: 'Comp Intelligence', icon: ChartBarIcon, featureKey: 'comp_intelligence' },
    // BF-T (2026-06-18): the /comps sub-pages had no nav entry, so
    // Matt could only reach "his reports" by drilling into one and
    // following back-links. Each entry's FeatureGate handles per-role
    // visibility — comp_requests is `any` so brokers see My Requests,
    // queue is manager+ so brokers don't see it. BF-MR (2026-06-25):
    // comp_reports is now also `any` and the page itself decides "All
    // Reports" vs "My Reports" based on role; the sidebar label
    // (`reportsLabel`) mirrors that choice.
    { path: '/comps/requests', label: 'My Requests', icon: ChartBarIcon, featureKey: 'comp_requests' },
    // Research Queue no longer has a sidebar entry (2026-07-08) — the
    // queue now renders as a permission-gated widget on /dashboard.
    { path: '/comps/reports', label: reportsLabel, icon: ChartBarIcon, featureKey: 'comp_reports' },
    { path: '/reviews', label: 'Reviews', icon: ClipboardDocumentCheckIcon, featureKey: 'reviews' },
    {
      path: '/reviews/submissions',
      label: 'Broker Submissions',
      icon: ClipboardDocumentCheckIcon,
      featureKey: 'reviews',
      badgeKind: 'pending_submissions',
    },
    {
      // F-TT (2026-06-19): the ad-hoc broker-submissions queue lives
      // at /reviews/broker-submissions and is research-tier only.
      // Distinct from /reviews/submissions (per-field suggestions +
      // property submissions); this one is free-form docs + notes.
      path: '/reviews/broker-submissions',
      label: 'Broker Docs & Notes',
      icon: ClipboardDocumentCheckIcon,
      featureKey: 'comp_research_queue',
      badgeKind: 'pending_broker_submissions',
    },
    // Documents — out of phase-1 scope per current spec; route + page kept,
    // hidden from the sidebar so it doesn't surface to users until the
    // feature is in scope. Re-enable by uncommenting.
    // { path: '/documents', label: 'Documents', icon: DocumentTextIcon, featureKey: 'documents' },
    { path: '/ops', label: 'Ops Dashboard', icon: WrenchScrewdriverIcon, featureKey: 'ops_dashboard' },
    { path: '/verification', label: 'Verification', icon: ShieldCheckIcon, featureKey: 'verification' },
    { path: '/ingestion', label: 'Data Ingestion', icon: ArrowUpTrayIcon, featureKey: 'data_ingestion' },
    { path: '/users', label: 'Manage Users', icon: UserGroupIcon, featureKey: 'manage_users' },
    { path: '/directory', label: 'Directory', icon: IdentificationIcon, featureKey: 'hr_directory' },
    // M286: only when the hub has an entry for this person (the same list it renders), so the item never opens an empty hub,
    // and they may open the hub: feature:settings, or Data Maintenance (Research's Land area mismatches, idea 15).
    ...(hasSettingsEntries && canOpenSettingsHub ? [{ path: '/settings', label: 'Settings', icon: Cog6ToothIcon }] : []),
    ...(isAdminOrAig
      ? [
          { path: '/devtools/admin-info', label: 'Admin Info', icon: ShieldCheckIcon },
          { path: '/devtools/calculated-fields', label: 'Calculated Fields', icon: CalculatorIcon },
        ]
      : []),
  ];

  // Super-admin (whitelisted email) entries — visible on every
  // environment, including remote/preview/prod, because the entire
  // point is to surface runtime errors caught in those environments.
  // No featureKey: gating is enforced by the `isSuperAdmin` check below
  // + by the backend route.
  const superAdminMenuItems: MenuItem[] = [
    // Devtools — internal changelog for client meetings. Ported from
    // cherie-app. NEVER for the client (decision 2026-07-20): visible
    // only to whitelisted internal emails, on local AND remote builds.
    // Was previously gated on `import.meta.env.DEV`; now the whitelist
    // is the gate everywhere. The backend enforces the same whitelist
    // on /api/release-notes*, so hiding the entry here is UX, not
    // security.
    {
      path: '/devtools/release-notes',
      label: 'Release Notes',
      icon: ClipboardDocumentCheckIcon,
      isDeveloperOnly: true,
    },
    {
      path: '/devtools/error-reports',
      label: 'Reported Errors',
      icon: WrenchScrewdriverIcon,
      isDeveloperOnly: true,
    },
  ];

  // Broker-tier users get a single-item sidebar (Dashboard only). The
  // broker dashboard already contains their assigned-properties card
  // + future submissions surfaces — there's no "Browse all properties /
  // Comp Intelligence / Reports" affordance they should be reaching from
  // the nav rail. Anything they need lives behind a card on /.
  // Request Tracker — tier-gated (see requestTrackerTier above). Appended
  // for any non-null tier, independent of isSuperAdmin / isBroker.
  const requestTrackerMenuItems: MenuItem[] =
    requestTrackerTier !== null
      ? [
          {
            path: '/request-tracker',
            label: 'Request Tracker',
            icon: ClipboardDocumentCheckIcon,
            // Tier-gated, not dev-only — a client tier sees this entry too, so
            // no "DEV" badge. Visibility is controlled by requestTrackerTier
            // (the item is only appended when tier !== null).
            isDeveloperOnly: false,
            // M263: a client's count of requests verified by AIG and waiting on their confirmation.
            ...(requestTrackerTier === 'client' ? { badgeKind: 'tracker_needs_confirmation' as const } : {}),
          },
        ]
      : [];

  // Listings (L1-3, L2-1): feature:listings (a Broker's own listing
  // agreements), listings_work or an admin — the same rule as ListingsRoute
  // in App.tsx (useCanSeeListings). Property management (PM-1): the
  // feature:property_management keys or an admin — PropertyManagementRoute's
  // rule (useCanSeePropertyManagement); was the admin-only preview. Forms:
  // the forms catalog (useCanSeeForms / useCanStartForms). Each rule is the
  // entry's gate, so none carries a featureKey (they used to borrow 'dashboard').
  const listingsMenuItems: MenuItem[] = [
    ...(canSeeListings ? [{ path: '/listings', label: 'Listings', icon: RectangleStackIcon }] : []),
    ...(canSeePropertyManagement
      ? [{ path: '/property-management', label: 'Property management', icon: BuildingOffice2Icon }]
      : []),
    ...(canSeeForms ? [{ path: '/forms', label: 'Forms', icon: ClipboardDocumentListIcon }] : []),
    ...(canStartForms ? [{ path: '/forms/start', label: 'Start a form', icon: DocumentPlusIcon }] : []),
  ];

  const baseItems = isBroker
    ? menuItems.filter((m) => m.path === '/')
    : menuItems;
  const allCandidateItems = [
    ...(isSuperAdmin ? [...baseItems, ...superAdminMenuItems] : baseItems),
    ...requestTrackerMenuItems,
    ...listingsMenuItems,
  ];

  return (
    // The outer <aside> reserves a constant 64px (w-16) of horizontal
    // space in the page flex flow — the icon rail width. The inner
    // panel grows to w-64 on hover via absolute positioning so the
    // expanded panel overlays the page content instead of reflowing
    // it on every cursor drift.
    //
    // `sticky top-0 h-screen self-start` keeps the sidebar pinned to
    // the top of the viewport regardless of how far down the user
    // scrolls. `self-start` prevents flex's default cross-axis
    // stretch from forcing the aside to match the row height (which
    // would defeat sticky positioning).
    <aside className="sticky top-0 self-start h-screen w-16 z-40">
      <div
        ref={sidebarRef}
        className={`absolute top-0 left-0 h-full text-white border-r border-gray-800 transition-[width] duration-200 ease-in-out overflow-y-auto overflow-x-hidden ${
          isExpanded ? 'w-64' : 'w-16'
        }`}
        // Inline style matches the header exactly. See Header.tsx for why.
        style={{ backgroundColor: '#111827' }}
      >
        {/* Navigation Menu */}
        <nav className="pt-4">
          <ul className="space-y-2 px-2">
            {allCandidateItems.map((item) => (
              <SidebarMenuItem
                key={item.path}
                item={item}
                isActive={location.pathname === item.path}
                isExpanded={isExpanded}
                activeBg={scheme.bg}
              />
            ))}
          </ul>
        </nav>

        {/* Footer: version stamp, only when expanded so the rail stays
            uncluttered. */}
        {isExpanded && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
            <div className="text-xs text-gray-400 whitespace-nowrap">
              <p>© 2026 Colliers International</p>
              <p className="mt-1">v0.1.0</p>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

/**
 * Sidebar entry that gates its own visibility on
 * `useFeatureAccess(item.featureKey)`. Extracted to a child so we
 * can call the hook once per item without violating rules of hooks
 * in a loop on the parent.
 */
/**
 * Headless count source for the 'pending_submissions' badge. Returns
 * null while loading / error / count === 0 so the badge dot only paints
 * when there's actually something to act on. Always called (no
 * conditional hooks), but the parent only renders the value when
 * item.badgeKind === 'pending_submissions'.
 */
function usePendingSubmissionsBadgeCount(enabled: boolean): number | null {
  // Only the 'Broker Submissions' sidebar entry passes enabled=true.
  //
  // Plan A (2026-06-21): the badge used to merge property_submissions
  // (pending) + field_change_suggestions (pending). After retiring
  // property_submissions, the badge is just a pending-suggestion count.
  //
  // M155: the server counts (GET /count?status=pending) — the old list
  // read stopped at 200 rows, so the badge could never pass 200.
  const { data: total = 0 } = useFieldChangeSuggestionsCount(
    enabled ? { status: 'pending' } : { id: NO_ROW_ID },
  );
  if (!enabled) return null;
  return total > 0 ? total : null;
}

/**
 * F-TT badge — count of broker_submissions in status SUBMITTED. Only
 * the 'Broker Docs & Notes' sidebar entry passes enabled=true. Once
 * research clicks Acknowledge / Accept / Reject the row falls out
 * of the count.
 */
function usePendingBrokerSubmissionsBadgeCount(enabled: boolean): number | null {
  const { data: statuses = [] } = useLookupSubmissionStatusesList({ limit: 20 });
  const submittedStatusId = (statuses as any[]).find((s) => s.code === 'SUBMITTED')?.id as
    | string
    | undefined;
  // M155: the server counts. The old read took the 200 oldest submissions
  // and counted SUBMITTED among them, so newer pending ones went uncounted.
  const { data: count = 0 } = useBrokerSubmissionsCount(
    enabled && submittedStatusId ? { submission_status_id: submittedStatusId } : { id: NO_ROW_ID },
  );
  if (!enabled || !submittedStatusId) return null;
  return count > 0 ? count : null;
}

/**
 * M263 badge — the client's requests verified by AIG and waiting on their
 * confirmation (GET /api/release-notes-funnel). Only the Request Tracker
 * entry of a CLIENT-tier user passes enabled=true; disabled, no call is made.
 */
function useTrackerNeedsConfirmationBadgeCount(enabled: boolean): number | null {
  const { data } = useTrackerFunnel(enabled);
  if (!enabled) return null;
  const n = data?.needs_confirmation ?? 0;
  return n > 0 ? n : null;
}

/** A filter no row matches: the disabled badges' cheap shared count. */
const NO_ROW_ID = '00000000-0000-0000-0000-000000000000';

function SidebarMenuItem({
  item,
  isActive,
  isExpanded,
  activeBg,
}: {
  item: MenuItem;
  isActive: boolean;
  isExpanded: boolean;
  activeBg: string;
}) {
  // Hooks run unconditionally: an entry without a featureKey asks a key it ignores.
  const keyAllows = useFeatureAccess(item.featureKey ?? 'dashboard');
  const canSee = item.featureKey ? keyAllows : true;
  const pendingSubmissionsCount = usePendingSubmissionsBadgeCount(
    item.badgeKind === 'pending_submissions',
  );
  const pendingBrokerSubmissionsCount = usePendingBrokerSubmissionsBadgeCount(
    item.badgeKind === 'pending_broker_submissions',
  );
  const trackerNeedsConfirmationCount = useTrackerNeedsConfirmationBadgeCount(
    item.badgeKind === 'tracker_needs_confirmation',
  );
  const badgeCount =
    item.badgeKind === 'pending_submissions'
      ? pendingSubmissionsCount
      : item.badgeKind === 'pending_broker_submissions'
        ? pendingBrokerSubmissionsCount
        : item.badgeKind === 'tracker_needs_confirmation'
          ? trackerNeedsConfirmationCount
          : null;
  if (!canSee) return null;
  const Icon = item.icon;
  return (
    <li>
      <Link
        to={item.path}
        className={`relative flex items-center px-3 py-2 rounded-lg transition-colors ${
          isActive
            ? `${activeBg} text-white`
            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
        }`}
        title={!isExpanded ? item.label : undefined}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {/* Collapsed-mode badge: a tiny red dot anchored to the icon's
            top-right so the manager sees something's waiting even when
            the rail is icon-only. */}
        {badgeCount != null && !isExpanded && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
        )}
        {isExpanded && (
          <span className="ml-3 text-sm font-medium whitespace-nowrap flex items-center gap-1.5">
            {item.label}
            {item.isDeveloperOnly && (
              <span className="ml-2 px-1 py-0.5 bg-yellow-600 text-yellow-100 text-xs rounded">
                DEV
              </span>
            )}
            {badgeCount != null && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full font-semibold min-w-[18px] text-center">
                {badgeCount > 99 ? '99+' : badgeCount}
              </span>
            )}
          </span>
        )}
      </Link>
    </li>
  );
}

export default Sidebar;
